/**
 * IDC (Indice de Dépense de Chaleur) Types
 * Canton de Genève - Heat Consumption Index
 */

/**
 * Distribution histogram bin
 */
export interface IDCDistributionBin {
  /** Start of the bin range (MJ/m²·year) */
  bin_start: number
  /** End of the bin range (MJ/m²·year) */
  bin_end: number
  /** Number of buildings in this bin */
  count: number
}

/**
 * IDC statistics for a comparison scope
 */
export interface IDCStatistics {
  /** Average IDC value in MJ/m²·year */
  avg: number
  /** Median IDC value in MJ/m²·year */
  median: number
  /** Minimum IDC value in MJ/m²·year */
  min: number
  /** Maximum IDC value in MJ/m²·year */
  max: number
  /** Number of buildings with IDC data in this scope */
  count: number
  /** 25th percentile (Q1) */
  percentile_25: number
  /** 75th percentile (Q3) */
  percentile_75: number
  /** Standard deviation (optional) */
  std_deviation?: number
}

/**
 * Base comparison scope information
 */
export interface IDCComparisonScope {
  /** Name of the scope (radius_500m, npa, municipality, region) */
  scope_name: string
  /** Human-readable label for the scope */
  scope_label: string
  /** Statistical data for this scope */
  statistics: IDCStatistics
  /** Distribution histogram data */
  distribution: IDCDistributionBin[]
}

/**
 * Radius-based comparison (500m around building)
 */
export interface IDCRadiusComparison extends IDCComparisonScope {
  scope_name: 'radius_500m'
  /** Radius in meters */
  radius_meters: number
}

/**
 * NPA (postal code) comparison
 */
export interface IDCNPAComparison extends IDCComparisonScope {
  scope_name: 'npa'
  /** Postal code (NPA) */
  npa_code: string
}

/**
 * Municipality comparison
 */
export interface IDCMunicipalityComparison extends IDCComparisonScope {
  scope_name: 'municipality'
  /** Municipality ID */
  municipality_id: string
  /** Municipality name */
  municipality_name: string
}

/**
 * Region (Canton) comparison
 */
export interface IDCRegionComparison extends IDCComparisonScope {
  scope_name: 'region'
  /** Region ID */
  region_id: string
  /** Region name (e.g., "Genève") */
  region_name: string
}

/**
 * Complete IDC comparisons response
 */
export interface IDCComparisons {
  /** The building's own IDC value in MJ/m²·year */
  building_idc: number
  /** Available comparison scopes */
  comparisons: {
    /** 500m radius comparison (optional) */
    radius_500m?: IDCRadiusComparison
    /** Postal code comparison (optional) */
    npa?: IDCNPAComparison
    /** Municipality comparison (optional) */
    municipality?: IDCMunicipalityComparison
    /** Region/Canton comparison (optional) */
    region?: IDCRegionComparison
  }
}

/**
 * Type for comparison scope selector
 */
export type IDCComparisonScopeType =
  | 'radius_500m'
  | 'npa'
  | 'municipality'
  | 'region'

/**
 * Helper to check if building is eligible for IDC comparisons
 */
export function hasIDCData(building: {
  heat_consumption_index?: number
  region_name?: string
}): boolean {
  return (
    building.heat_consumption_index != null &&
    building.heat_consumption_index > 0 &&
    building.region_name === 'Genève'
  )
}

/**
 * Calculate percentile rank of a value in a distribution
 */
export function calculatePercentileRank(
  value: number,
  statistics: IDCStatistics
): number {
  // Estimate percentile using min, q25, median, q75, max
  const { min, percentile_25, median, percentile_75, max } = statistics

  if (value <= min) return 0
  if (value >= max) return 100

  if (value < percentile_25) {
    // Between min and Q1
    return ((value - min) / (percentile_25 - min)) * 25
  } else if (value < median) {
    // Between Q1 and Q2
    return 25 + ((value - percentile_25) / (median - percentile_25)) * 25
  } else if (value < percentile_75) {
    // Between Q2 and Q3
    return 50 + ((value - median) / (percentile_75 - median)) * 25
  } else {
    // Between Q3 and max
    return 75 + ((value - percentile_75) / (max - percentile_75)) * 25
  }
}

/**
 * Get performance category based on percentile
 */
export function getPerformanceCategory(percentileRank: number): {
  category: 'excellent' | 'good' | 'average' | 'poor' | 'critical'
  label: string
  color: string
} {
  if (percentileRank <= 25) {
    return {
      category: 'excellent',
      label: 'Excellent (25% meilleurs)',
      color: '#10b981',
    }
  } else if (percentileRank <= 50) {
    return {
      category: 'good',
      label: 'Bon (50% meilleurs)',
      color: '#3b82f6',
    }
  } else if (percentileRank <= 75) {
    return {
      category: 'average',
      label: 'Moyen',
      color: '#f59e0b',
    }
  } else if (percentileRank <= 90) {
    return {
      category: 'poor',
      label: 'Élevé',
      color: '#f97316',
    }
  } else {
    return {
      category: 'critical',
      label: 'Très élevé (10% plus élevés)',
      color: '#ef4444',
    }
  }
}
